import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { BodyText, LinkText } from '../../typography';
import { TableSize } from '../types';
import { SvgComponent } from '../../../assets';

export type SummaryTableRowProps = SummaryTableRowData & {
  size: TableSize;
  multiline: boolean;
};

export type SummaryTableRowData = {
  label: string;
  value: string;
  icon?: SvgComponent;
  valueHref?: string;
  truncatable?: boolean;
  editHref?: string;
  isVerification?: boolean;
  onClick?: () => void;
};

const Row = styled.div<{ size: TableSize; multiline: boolean }>`
  padding: ${theme.spacings[2].rem} 0;
  width: 100%;
  &:first-of-type {
    display: flex;
    ${({ multiline }) => multiline && 'flex-flow: column wrap;'}
  }

  &:not(:first-of-type) {
    border-top: 1px solid ${theme.colors.neutrals.transparentGrays[6]};
    ${({ size }) => size === 'large' && 'display: flex;'}
    ${({ multiline }) => multiline && 'flex-flow: column wrap;'}
  }
`;

const Info = styled.div`
  display: flex;
  max-width: 100%;
`;

const LabelWrapper = styled.span`
  display: flex;
  justify-content: space-between;
`;

const Label = styled(BodyText)`
  ${({ size }) => size === 'sm' && `margin-bottom: ${theme.spacings[1].rem};`}
  font-weight: 700;
`;

const IconWrapper = styled.span<{ size: TableSize }>`
  flex: none;
  fill: ${theme.colors.primary.graysGray};
  height: ${({ size }) => (size === 'small' ? '16' : '24')}px;
  margin-right: ${({ size }) => theme.spacings[size === 'small' ? 1 : 2].rem};
  width: ${({ size }) => (size === 'small' ? '16' : '24')}px;
`;

const Text = styled(BodyText)`
  align-items: center;
`;

const TruncatableText = styled(BodyText)`
  align-items: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: ${theme.spacings[4].rem};
`;

const Link = styled(LinkText)`
  margin-left: ${theme.spacings[3].rem};
`;

const StyledLinkText = styled(LinkText)<{ isVerification?: boolean }>`
  text-decoration-color: ${theme.colors.primary.graysGray};
  ${({ isVerification }) =>
    isVerification &&
    `
      text-decoration: auto;
    `}

  p {
    ${({ isVerification }) =>
      isVerification &&
      `
        color: ${theme.colors.primary.graysBlue};
      `}
  }
`;
export const SummaryTableRow: React.FC<SummaryTableRowProps> = ({
  label,
  editHref,
  onClick,
  icon: Icon,
  size,
  value,
  valueHref,
  truncatable,
  multiline,
  isVerification,
}) => {
  const textSize = size === 'small' ? 'sm' : 'md';
  const text = truncatable ? (
    <TruncatableText size={textSize}>{value}</TruncatableText>
  ) : (
    <Text size={textSize}>{value}</Text>
  );
  return (
    <Row size={size} multiline={multiline}>
      <LabelWrapper>
        <Label size={textSize}>{label}</Label>
        {editHref && (
          <Link href={editHref} size={textSize} onClick={onClick}>
            Edit
          </Link>
        )}
      </LabelWrapper>
      <Info>
        {Icon && (
          <IconWrapper size={size}>
            <Icon />
          </IconWrapper>
        )}
        {valueHref ? (
          <StyledLinkText
            isVerification={isVerification}
            href={valueHref}
            size={textSize}
            target="_blank"
            isUnderlined
          >
            {text}
          </StyledLinkText>
        ) : (
          text
        )}
      </Info>
    </Row>
  );
};
